import clsx from 'clsx';
import type { PropsWithChildren } from 'react';

import { Flex } from '../../../../../layouts/flex/Flex.js';
import { interactionsOverlay } from '../../../../core/components/interactions-overlay/InteractionsOverlay.sty.js';
import { useOverlay } from '../../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useTooltipData } from '../../../../core/components/overlay-tooltip/hooks/useTooltipData.js';
import { OverlayTooltip } from '../../../../core/components/overlay-tooltip/OverlayTooltip.js';
import type { Data } from '../../../../core/components/overlay-tooltip/types.js';
import { useTooltipActions } from '../../../../core/components/overlay-tooltip/useTooltipActions.js';
import { isTooltipDataEmpty } from '../../../../core/components/overlay-tooltip/utils.js';
import { useSeriesActions } from '../../../../core/components/series-action/ChartSeriesActionContext.js';
import { DEFAULT_VALUE_ACCESSOR } from '../../../../core/constants.js';
import { MeterBarChartTooltip } from '../../../shared/components/meter-bar-tooltip/MeterBarChartTooltip.js';
import { useFormatter } from '../../../shared/context/FormatterContext.js';
import { useMeterBarLayoutSize } from '../../../shared/context/MeterBarLayoutSizeContext.js';
import { elementSize } from '../../../shared/styles/element-size.sty.js';
import { container } from '../../../shared/styles.sty.js';
import { getNumericValue } from '../../../shared/utils/get-numeric-value.js';
import { useMeterBarChartData } from '../../context/MeterBarChartDataContext.js';
import { useMeterBarChartInternalConfig } from '../../context/MeterBarChartInternalConfigContext.js';
import type {
  MeterBarChartInternalConfig,
  TooltipConfigData,
} from '../../types/meter-bar-chart-internal.js';

type CustomTooltipProps = {
  template: NonNullable<TooltipConfigData['template']>;
  displayValue: number;
  min: number;
  max: number;
  color: string;
  name: string;
  thresholds: MeterBarChartInternalConfig['thresholds'] | undefined;
};

const MeterBarCustomTooltip = ({
  template,
  displayValue,
  min,
  max,
  color,
  name,
  thresholds,
}: CustomTooltipProps) => {
  const activeThreshold = thresholds?.data
    ?.slice()
    .sort((a, b) => b.value - a.value)
    .find((t) => displayValue >= t.value);
  return (
    <OverlayTooltip
      template={template({
        value: displayValue,
        min,
        max,
        color,
        name,
        threshold: activeThreshold
          ? {
              label: activeThreshold.name,
              color: activeThreshold.color ?? color,
            }
          : undefined,
      })}
    />
  );
};

export const MeterBarChartOverlayWithTooltip = (props: PropsWithChildren) => {
  const { size } = useMeterBarLayoutSize();
  const { color, name, value, valueAccessor } = useMeterBarChartData();
  const {
    tooltip: tooltipConfig,
    min,
    max,
    thresholds,
  } = useMeterBarChartInternalConfig();
  const { formatter, unit } = useFormatter();
  const { chartSeriesActions } = useSeriesActions();
  const overlay = useOverlay();
  const { hover, togglePin, outOfBounds } = useTooltipActions();
  const { data: tooltipData } = useTooltipData();

  const displayValue = getNumericValue(
    value,
    valueAccessor ?? DEFAULT_VALUE_ACCESSOR,
  );
  const item = {
    name: tooltipConfig?.name ?? name ?? '',
    value: formatter(displayValue, unit),
  };
  const data: Data = {
    geometry: 'meterbar' as const,
    series: [
      {
        name: item.name,
        value: item.value,
        color,
      },
    ],
  };

  const tooltipContent =
    tooltipConfig?.template && !isTooltipDataEmpty(tooltipData) ? (
      <MeterBarCustomTooltip
        template={tooltipConfig.template}
        displayValue={displayValue}
        min={min}
        max={max}
        color={color}
        name={tooltipConfig.name ?? item.name}
        thresholds={thresholds}
      />
    ) : (
      <MeterBarChartTooltip
        actionsForItem={
          chartSeriesActions
            ? () => () =>
                chartSeriesActions({
                  id: `${name}-${displayValue}`,
                  name: item.name,
                  values: [item.value],
                })
            : undefined
        }
      />
    );

  return (
    <Flex
      ref={overlay.overlayRef}
      className={clsx(container, elementSize({ size }), interactionsOverlay)}
      data-testid="overlay-wrapper"
      onPointerMove={(event) => hover(event, data)}
      onPointerLeave={outOfBounds}
      onClick={(event) => togglePin(event, data)}
    >
      {tooltipContent}
      {props.children}
    </Flex>
  );
};

MeterBarChartOverlayWithTooltip['displayName'] =
  'MeterBarChartOverlayWithTooltip';
